import { InlineAlertType } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { isValidAmountParam } from 'features/billSwitch/helpers/amountValidator';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { redirectToHomePage } from 'shared/helpers/backNavigation.helpers';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';

const BillSwitchBelieveFundingScreen = () => {
  const isBillSwitchEnabled = useIsFeatureEnabled(
    FeatureFlagKey.BILL_SWITCH_PILOT,
  );
  const { t: translatedText } = useTranslation('billSwitch', {
    keyPrefix: 'fundingScreenBelieve',
  });
  const router = useRouter();
  const { amt } = useLocalSearchParams<{
    amt?: string;
  }>();

  useViewDidFocusLogScreenAnalytics(
    isBillSwitchEnabled
      ? {
          name: 'Bill Switch Funding - Believe',
          properties: {
            category: 'bill_switch',
            screen: 'believe_funding',
            source_name: 'home_believe',
            estimated_bill_amount: amt ?? 'unknown',
          },
        }
      : null,
  );

  if (!isBillSwitchEnabled) {
    return <Redirect href="/" />;
  }

  const fundBelieve = () => {
    router.replace({
      pathname: '/move-money/transfer/transfer-money',
      params: {
        typeOfAccount: 'CHARGE_CARD',
      },
    });
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
              primaryOnPress={redirectToHomePage}
            />
          ),
        }}
      />
      <ActionScreen
        titleText={translatedText('title')}
        titleTextStyle="extra-large"
        visual="BILLS_A"
        bodyContent={
          <View style={styles.bodyContainer}>
            <BodyBaseText>{translatedText('body')}</BodyBaseText>
            {isValidAmountParam(amt) ? (
              <InlineAlert
                alertType={InlineAlertType.Info}
                isImageVisible={false}
                titleText={translatedText('alert.title')}
                bodyText={'$' + amt}
              />
            ) : null}
          </View>
        }
        primaryButton={{
          label: translatedText('cta.primary'),
          onPress: fundBelieve,
          analyticsId: 'primaryButton',
          customAttributes: {
            source_name: 'home_believe',
          },
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    gap: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default BillSwitchBelieveFundingScreen;
